import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import {
  IconDashboard, IconContacts, IconGroups, IconBlast, IconCampaigns,
  IconFubCampaigns, IconTemplates, IconInbox, IconAiDrafts, IconTeam,
  IconSettings, IconHelp, IconLogout,
} from "./Icons";

const NAV_ITEMS = [
  { to: "/dashboard", icon: IconDashboard, label: "Dashboard", exact: true },
  {
    icon: IconContacts, label: "Contacts",
    children: [
      { to: "/contacts", icon: IconContacts, label: "All Contacts" },
      { to: "/groups", icon: IconGroups, label: "Groups" },
    ],
  },
  { to: "/blast", icon: IconBlast, label: "Mass Blast" },
  {
    icon: IconCampaigns, label: "Campaigns",
    children: [
      { to: "/campaigns", icon: IconCampaigns, label: "Campaigns" },
      { to: "/fub-campaigns", icon: IconFubCampaigns, label: "FUB Campaigns" },
    ],
  },
  { to: "/templates", icon: IconTemplates, label: "Templates" },
  { to: "/inbox", icon: IconInbox, label: "Inbox" },
  { to: "/ai-drafts", icon: IconAiDrafts, label: "AI Drafts" },
];

const BOTTOM_NAV = [
  { to: "/team", icon: IconTeam, label: "Team" },
  { to: "/settings", icon: IconSettings, label: "Settings" },
  { to: "/help", icon: IconHelp, label: "Help & FAQ" },
];

function NavDropdown({ item, collapsed, onNavClick }) {
  const location = useLocation();
  const isChildActive = item.children.some(c => location.pathname === c.to);
  const [open, setOpen] = useState(isChildActive);
  const Icon = item.icon;

  const isExpanded = open || isChildActive;

  return (
    <div>
      <button
        onClick={() => !collapsed && setOpen(!open)}
        className="sidebar-link w-full text-left"
        style={{
          justifyContent: "space-between",
          background: isExpanded
            ? "linear-gradient(135deg, rgba(255,92,53,0.12) 0%, rgba(255,92,53,0.06) 100%)"
            : undefined,
          color: isExpanded ? "#FF5C35" : undefined,
          fontWeight: isExpanded ? 600 : undefined,
        }}
      >
        <div className="flex items-center gap-3">
          <Icon size={18} color={isExpanded ? "#FF5C35" : "white"} />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </div>
        {!collapsed && (
          <ChevronDown
            size={14}
            className="shrink-0 transition-transform duration-200"
            style={{
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              opacity: isExpanded ? 0.8 : 0.4,
            }}
          />
        )}
      </button>

      {!collapsed && isExpanded && (
        <div className="ml-4 mt-1 space-y-1 pr-2">
          {item.children.map(child => {
            const ChildIcon = child.icon;
            return (
              <NavLink
                key={child.to}
                to={child.to}
                onClick={onNavClick}
                className="flex items-center gap-2.5 px-4 py-2 transition-all duration-200 cursor-pointer"
                style={({ isActive }) => ({
                  color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.45)",
                  borderRadius: "53px",
                  background: isActive ? "#FF5C35" : "transparent",
                  letterSpacing: "0.08em",
                  fontSize: "12px",
                  fontWeight: isActive ? 600 : 500,
                })}
                data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <ChildIcon size={14} color={location.pathname === child.to ? "white" : "rgba(255,255,255,0.45)"} />
                <span>{child.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { unreadCount } = useNotifications();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside
      className="flex flex-col h-screen transition-all duration-300 relative shrink-0"
      style={{
        width: collapsed ? "72px" : "260px",
        background: "linear-gradient(180deg, #101418 0%, #172937 100%)",
        borderRadius: "0 0 50px 0",
        filter: "drop-shadow(4px 4px 12px rgba(0,0,0,0.25))",
        overflow: "visible",
      }}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="flex items-center justify-center px-4 py-6">
        <img
          src="/logo-white.svg"
          alt="Relay"
          className={`shrink-0 transition-all duration-300 ${collapsed ? "h-8 w-8" : "w-28"}`}
        />
      </div>

      {/* Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute top-16 w-7 h-7 rounded-full hidden md:flex items-center justify-center transition-all duration-200 z-50 hover:scale-110"
        style={{
          right: "-14px",
          background: "linear-gradient(135deg, #1C3040 0%, #2A4A5E 100%)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          overflow: "visible",
        }}
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight size={13} color="rgba(255,255,255,0.7)" /> : <ChevronLeft size={13} color="rgba(255,255,255,0.7)" />}
      </button>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          if (item.children) {
            return (
              <NavDropdown
                key={item.label}
                item={item}
                collapsed={collapsed}
                onNavClick={handleNavClick}
              />
            );
          }

          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              onClick={handleNavClick}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="relative shrink-0">
                <Icon size={18} />
                {item.label === "Inbox" && unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#FF5C35" }} />
                )}
              </div>
              {!collapsed && (
                <span className="truncate flex-1">{item.label}</span>
              )}
              {!collapsed && item.label === "Inbox" && unreadCount > 0 && (
                <span
                  className="ml-auto min-w-[20px] h-5 flex items-center justify-center rounded-full text-[11px] font-semibold text-white px-1.5"
                  style={{ backgroundColor: "#FF5C35" }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="px-3 pt-3 space-y-1" style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingBottom: "60px" }}>
        {BOTTOM_NAV.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={handleNavClick}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Icon size={18} />
            {!collapsed && <span className="truncate">{label}</span>}
          </NavLink>
        ))}

        {!collapsed && user && (
          <div className="px-4 py-2 mt-1">
            <p className="text-[10px] truncate" style={{ color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", fontFamily: "Montserrat, sans-serif" }}>
              {user.email}
            </p>
          </div>
        )}

        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2 mt-1 text-white text-xs font-medium uppercase transition-all duration-200"
          style={{
            backgroundColor: "#FF5C35",
            borderRadius: "8px",
            letterSpacing: "0.1em",
            fontFamily: "Montserrat, sans-serif",
          }}
          data-testid="logout-button"
        >
          <IconLogout size={14} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
